// Component: AuthLayout.jsx
// Purpose: Shared visual shell for the auth screens (Login today; Sign-up /
//   Forgot-password reuse this once they exist as real pages — see the note
//   in Login.jsx). Ambient brand-gradient glow behind a frosted GlassPanel
//   card, real brand lockup up top. Pure presentation: callers own all auth
//   logic — this component takes no auth state and fires no handlers.
//   Styling: ./auth.css (page-local, not part of the DS primitive layer).
//
//   Responsive: ≥768 keeps the centered glass card over the glow. <768 the
//   form docks to the bottom as a frosted sheet that slides up on mount
//   (SPRING_SMOOTH); the brand lockup stays on the hero, above the sheet.
//   The /login sheet is never dismissible — no grabber, no drag, no
//   backdrop-close (signing in is the only way forward).

import React from "react";
import { motion } from "framer-motion";
import { GlassPanel, SPRING_SMOOTH, cx } from "../../components/ds";
import { useBreakpoint } from "../../hooks/useBreakpoint";
//* Real brand mark — same asset + lockup as Sidebar.jsx / NavRail.jsx /
//  LandingPage.jsx nav (the "dt" monogram, single asset for both themes).
import brandMark from "../../data/logo-icon-light.png";
import "./auth.css";

const GLOW_ENTRANCE = { duration: 0.24, ease: [0.2, 0, 0, 1] };

// Shared lockup — logo image + "DeeTrade" wordmark, same pairing the sidebar
// nav uses. No gradient-text treatment; plain themed ink (var(--fg-1)).
export function AuthLockup({ className }) {
  return (
    <div className={cx("ds-auth-lockup", className)}>
      <img className="ds-auth-lockup-mark" src={brandMark} alt="" aria-hidden="true" />
      <span className="ds-auth-lockup-word">DeeTrade</span>
    </div>
  );
}

export default function AuthLayout({
  eyebrow = "ALGORITHMIC TRADING PLATFORM",
  children,
}) {
  const { isPhone } = useBreakpoint();

  if (isPhone) {
    return (
      <div className="ds-auth-page ds-auth-page--phone">
        <motion.div
          className="ds-auth-glow"
          aria-hidden="true"
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={GLOW_ENTRANCE}
        />

        {/* Brand lockup lives on the hero (over the glow), not in the sheet. */}
        <div className="ds-auth-hero">
          <AuthLockup />
          <p className="ds-auth-eyebrow">{eyebrow}</p>
        </div>

        {/* Form sheet — docks to the bottom and slides up on mount. Not
            dismissible: no grabber, no drag, no backdrop-close. */}
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          transition={SPRING_SMOOTH}
          style={{ position: "relative", zIndex: 1, width: "100%" }}
        >
          <GlassPanel className="ds-auth-sheet">{children}</GlassPanel>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="ds-auth-page">
      {/* Ambient brand-hero glow — fades/scales in on mount, then drifts
          slowly forever via CSS (see .ds-auth-glow in auth.css). */}
      <motion.div
        className="ds-auth-glow"
        aria-hidden="true"
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={GLOW_ENTRANCE}
      />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={SPRING_SMOOTH}
        style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 400 }}
      >
        <GlassPanel className="ds-auth-card">
          <AuthLockup className="ds-auth-lockup--card" />
          <p className="ds-auth-eyebrow">{eyebrow}</p>
          {children}
        </GlassPanel>
      </motion.div>
    </div>
  );
}
